// The CO Log PSR view's Excel export - the same workbook co_log_build.py writes
// to the Volta-grid site, built in the browser from what is on screen.
//
// One sheet, laid out like the page: an "Approved Change Orders" block and a
// "Non-Approved Change Orders" block, each with the navy column header, zebra
// rows and a running cumulative revenue. Internal changes are left out - the
// file is what goes to the client - so the sections passed in are filtered
// here rather than trusting the caller.
//
// Columns: CO No., Description, Status, Initiated, Submitted, Signed, Revenue,
// Cumulative Rev, Days Extended, Notes. Cumulative Rev is a live formula so a
// row typed in later still rolls up, and an "Approved to date" strip above the
// blocks sums the approved revenue and days.
import moment from 'moment';
import { pNum } from '../db/company';

const NAVY = 'FF173659', NAVY_DARK = 'FF0F2A4A', GRID = 'FFB7C4D3', ZEBRA = 'FFF6F8FB', CELL = 'FF0E1B2B';
const MONEY = '$#,##0.00;($#,##0.00);-';
const HEADS = ['CO No.', 'Description', 'Status', 'Initiated', 'Submitted', 'Signed',
    'Revenue', 'Cumulative Rev', 'Days Extended', 'Notes'];
const WIDTHS = [10, 46, 15, 13, 13, 13, 17, 18, 14, 60];
const RIGHT = new Set([7, 8, 9]);
const BLANK_ROWS = 5;
const TITLES = { approved: 'Approved Change Orders', other: 'Non-Approved Change Orders' };
const JOB_NAMES = { 54026070: 'QPAC Blitz Acceleration', 54026071: 'Block 4 (Mechanical, Structural & I&E Work)' };

const asDate = (v) => {
    if (!v) return null;
    const m = moment.utc(v);
    return m.isValid() ? new Date(Date.UTC(m.year(), m.month(), m.date())) : null;
};
const border = (color = GRID) => {
    const s = { style: 'thin', color: { argb: color } };
    return { left: s, right: s, top: s, bottom: s };
};

// sections: the PSR view's [{type: 'approved'|'other'|'internal-change', rows}]
export const buildCoLogWorkbook = async (sections, meta = {}) => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Strike';
    const ws = wb.addWorksheet('Change Order Log', {
        views: [{ state: 'frozen', ySplit: 5, showGridLines: false }],
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });
    WIDTHS.forEach((w, i) => { ws.getColumn(i + 1).width = w; });
    const job = meta.job || '';
    ws.getCell('A1').value = 'Change Order Log';
    ws.getCell('A1').font = { name: 'Arial', size: 16, bold: true, color: { argb: CELL } };
    ws.getCell('A2').value = `Strike  ·  ${meta.client || 'VoltaGrid LLC'}  ·  Job ${job}  ·  ${meta.jobName || JOB_NAMES[job] || ''}`;
    ws.getCell('A2').font = { name: 'Arial', size: 10, color: { argb: 'FF475569' } };
    ws.getCell('A3').value = 'One row per change order. Cumulative revenue runs within each block; the totals above update as rows are added.';
    ws.getCell('A3').font = { name: 'Arial', size: 9, italic: true, color: { argb: 'FF64748B' } };

    const byType = Object.fromEntries((sections || []).map((s) => [s.type, s.rows || []]));
    let r = 6;
    const ranges = {};
    for (const key of ['approved', 'other']) {
        const rows = byType[key] || [];
        const title = ws.getCell(r, 1);
        title.value = TITLES[key];
        title.font = { name: 'Arial', size: 14, bold: true, color: { argb: CELL } };
        ws.getRow(r).height = 22;
        r += 1;
        HEADS.forEach((h, i) => {
            const c = ws.getCell(r, i + 1);
            c.value = h;
            c.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
            c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
            c.border = { ...border('FFAEADAF'), top: { style: 'thin', color: { argb: NAVY_DARK } }, bottom: { style: 'thin', color: { argb: NAVY_DARK } } };
            c.alignment = { horizontal: RIGHT.has(i + 1) ? 'right' : 'left', vertical: 'middle', indent: 1 };
        });
        ws.getRow(r).height = 24;
        r += 1;
        const first = r;
        const n = Math.max(rows.length, BLANK_ROWS);
        for (let idx = 0; idx < n; idx += 1) {
            const row = rows[idx];
            const vals = row ? [
                row.Row_Label ?? '', row.Description ?? '', row.Status ?? '',
                asDate(row.Initiated), asDate(row.Submitted), asDate(row.Signed),
                Math.round(pNum(row.Revenue || 0) * 100) / 100, null, pNum(row.Extension || 0) || null, row.Notes ?? '',
            ] : new Array(HEADS.length).fill(null);
            vals.forEach((v, i) => {
                const col = i + 1;
                const c = ws.getCell(r, col);
                c.value = v;
                c.font = { name: 'Arial', size: 10, color: { argb: CELL } };
                c.border = border();
                if (idx % 2) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ZEBRA } };
                c.alignment = { vertical: 'top', wrapText: col === 2 || col === 10, horizontal: col === 1 ? 'center' : RIGHT.has(col) ? 'right' : 'left' };
                if (col >= 4 && col <= 6) c.numFmt = 'm/d/yyyy';
                if (col === 7 || col === 8) c.numFmt = MONEY;
                if (col === 9) c.numFmt = '0;-0;-';
            });
            const prev = r > first ? `N(H${r - 1})` : '0';
            const cum = ws.getCell(r, 8);
            cum.value = { formula: `IF(A${r}="","",${prev}+N(G${r}))` };
            cum.font = { name: 'Arial', size: 10, bold: true, color: { argb: CELL } };
            r += 1;
        }
        ranges[key] = [first, r - 1];
        r += 2;
    }

    const [a0, a1] = ranges.approved;
    const strip = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF1F5F9' } };
    const stripBorder = { top: { style: 'thin', color: { argb: 'FFCBD5E1' } }, bottom: { style: 'thin', color: { argb: 'FFCBD5E1' } } };
    const label = ws.getCell(4, 6);
    label.value = 'Approved to date';
    label.font = { name: 'Arial', size: 10, bold: true, color: { argb: CELL } };
    label.alignment = { horizontal: 'right' };
    ws.getCell(4, 7).value = { formula: `SUM(G${a0}:G${a1})` };
    ws.getCell(4, 7).numFmt = MONEY;
    ws.getCell(4, 9).value = { formula: `SUM(I${a0}:I${a1})` };
    ws.getCell(4, 9).numFmt = '0 "days";-0 "days";-';
    [6, 7, 8, 9].forEach((col) => {
        const c = ws.getCell(4, col);
        c.fill = strip; c.border = stripBorder;
        if (col !== 6) c.font = { name: 'Arial', size: 10, bold: true, color: { argb: CELL } };
    });
    return wb;
};

export const downloadCoLog = async (sections, meta = {}) => {
    const wb = await buildCoLogWorkbook(sections, meta);
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const name = `${meta.job || 'job'} Change Order Log ${moment().format('YYYY-MM-DD')}.xlsx`;
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    return name;
};
